import firebase from '../config/firebase.config';
import moment from 'moment';
import httpClient from '../config/httpClient';

const database = firebase.firestore();
/**
 * Fetches attendance data for all members for a specific date
 * @param {string} companyId - The company ID
 * @param {string} date - The date in any format (will be converted to DD:MM:YYYY)
 * @returns {Promise<Array>} Array of member attendance data
 */
const getAllMembersAttendance = async (companyId, date) => {
  try {
    const formattedDate = moment(date).format('DD:MM:YYYY');

    // Get all members collection
    const membersRef = database
      .collection('variant2_WfhGO_DB')
      .doc(companyId)
      .collection('members');

    // Get all members documents
    const membersSnapshot = await membersRef.get();

    if (membersSnapshot.empty) {
      return [];
    }

    // Create an array of promises to fetch each member's attendance data
    const attendancePromises = membersSnapshot.docs.map(async (memberDoc) => {
      const memberId = memberDoc.id;
      const memberData = memberDoc.data();

      // Get the day document for the specific date
      const dayRef = memberDoc.ref.collection('days').doc(formattedDate);

      const daySnapshot = await dayRef.get();
      const dayData = daySnapshot.data();

      return {
        memberId,
        name: memberData.name || 'Unknown',
        email: memberData.email || '',
        attendance: dayData?.calculatedTimelog || {
          punchIn: null,
          punchOut: null,
          totalTimeTracked: 0,
          productiveHours: 0,
          idleTime: 0,
          breakTime: 0,
        },
      };
    });

    // Execute all promises in parallel
    const results = await Promise.all(attendancePromises);
    return results;
  } catch (error) {
    console.error('Error fetching all members attendance:', error);
    throw new Error('Failed to fetch members attendance data');
  }
};

/**
 * Fetches attendance data for a single member
 * @param {string} companyId - The company ID
 * @param {string} memberId - The member ID
 * @param {string} date - The date in any format (will be converted to DD:MM:YYYY)
 * @returns {Promise<Object>} Member's attendance data
 */
const getMemberAttendance = async (companyId, memberId, date) => {
  try {
    const formattedDate = moment(date).format('DD:MM:YYYY');

    const dayRef = database
      .collection('variant2_WfhGO_DB')
      .doc(companyId)
      .collection('members')
      .doc(memberId)
      .collection('days')
      .doc(formattedDate);

    const daySnapshot = await dayRef.get();
    const dayData = daySnapshot.data();

    if (dayData && dayData.calculatedTimelog) {
      return dayData.calculatedTimelog;
    }

    return {
      punchIn: null,
      punchOut: null,
      totalTimeTracked: 0,
      productiveHours: 0,
      idleTime: 0,
      breakTime: 0,
    };
  } catch (error) {
    console.error('Error fetching member attendance:', error);
    throw new Error('Failed to fetch member attendance data');
  }
};

/**
 * Fetches attendance data for all members via API
 * @param {string} companyId - The company ID
 * @param {string} date - The date in any format (will be converted to MM/DD/YYYY)
 * @returns {Promise<Array>} Array of member attendance data
 */
const getAllMembersAttendaceViaApi = async (companyId, date) => {
  const formattedDate = moment(date).format('MM/DD/YYYY');
  try {
    const response = await httpClient.post(
      '/getAttendanceData',
      {
        companyId: companyId,
        date: formattedDate,
      },
      {
        headers: {
          Authorization: '20241211012026_057',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching all members attendance:', error);
    throw new Error('Failed to fetch members attendance data');
  }
};

export default {
  getAllMembersAttendance,
  getMemberAttendance,
  getAllMembersAttendaceViaApi,
};
